import Constants from 'expo-constants';
import { NativeModules, Platform } from 'react-native';

import {
  devServerOriginFromHostUri,
  isLoopbackHostname,
  resolveLocalUrlForPlatform,
  type DevServerOrigin,
} from './local-url-core';

type SourceCodeModule = Readonly<{
  scriptURL?: string;
}>;

type ExpoConstantsWithDevHosts = typeof Constants & {
  expoConfig?: { hostUri?: string };
  manifest?: { debuggerHost?: string };
  manifest2?: { extra?: { expoClient?: { hostUri?: string } } };
};

const loggedResolutions = new Set<string>();

function hostnameFromHostUri(hostUri: string | undefined): string | null {
  if (!hostUri) {
    return null;
  }

  try {
    return new URL(hostUri).hostname || null;
  } catch {
    return hostUri.split(':')[0] || null;
  }
}

function getNativeDevHostMetadata() {
  const constants = Constants as ExpoConstantsWithDevHosts;
  const sourceCode = NativeModules.SourceCode as SourceCodeModule | undefined;
  const scriptURL = sourceCode?.scriptURL;
  let scriptHostname: string | null = null;

  if (scriptURL) {
    try {
      const bundleUrl = new URL(scriptURL);
      scriptHostname = bundleUrl.hostname || null;
    } catch {
      scriptHostname = null;
    }
  }

  const expoConfigHostUri = constants.expoConfig?.hostUri;
  const manifestDebuggerHost = constants.manifest?.debuggerHost;
  const manifest2HostUri = constants.manifest2?.extra?.expoClient?.hostUri;
  const devServerOrigins = [
    devServerOriginFromHostUri(scriptURL, 'scriptURL'),
    devServerOriginFromHostUri(expoConfigHostUri, 'expoConfig.hostUri'),
    devServerOriginFromHostUri(manifestDebuggerHost, 'manifest.debuggerHost'),
    devServerOriginFromHostUri(manifest2HostUri, 'manifest2.extra.expoClient.hostUri'),
  ].filter((origin): origin is DevServerOrigin => Boolean(origin));
  const hostCandidates = [
    scriptHostname,
    hostnameFromHostUri(expoConfigHostUri),
    hostnameFromHostUri(manifestDebuggerHost),
    hostnameFromHostUri(manifest2HostUri),
  ].filter((candidate): candidate is string => Boolean(candidate));
  const devServerOrigin =
    devServerOrigins.find((origin) => !isLoopbackHostname(origin.hostname)) ?? null;

  return {
    chosenHostname: devServerOrigin?.hostname ?? null,
    devServerOrigin,
    devServerOrigins,
    expoConfigHostUri,
    hostCandidates,
    manifest2HostUri,
    manifestDebuggerHost,
    scriptHostname,
    scriptURL,
  };
}

function debugLocalUrlResolution(context: string, details: Record<string, unknown>) {
  if (!__DEV__) {
    return;
  }

  const logKey = JSON.stringify({
    context,
    inputUrl: details.inputUrl,
    resolvedUrl: details.resolvedUrl,
  });
  if (loggedResolutions.has(logKey)) {
    return;
  }

  loggedResolutions.add(logKey);
  console.info(`[6am local-url] ${context}`, details);
}

/**
 * The Android emulator runs in its own VM, so `127.0.0.1`/`localhost` points at
 * the emulator itself rather than the host machine. The host loopback is reached
 * via the special `10.0.2.2` alias. iOS simulators and web share the host
 * network, so they need no rewriting.
 *
 * Physical devices opened through a LAN Metro URL cannot reach the host machine
 * through loopback. In that case, reuse the native bundle host and keep the
 * original service port, so one `.env.local` can support simulators and network
 * devices.
 *
 * Physical devices opened through a Tailscale Serve HTTPS Metro URL also need
 * HTTPS for API and Supabase calls. For that mode, the Metro port is served on
 * 8443 and local Supabase is served on 8444.
 *
 * This only rewrites a localhost URL, so remote URLs (production) are returned
 * unchanged.
 */
export function resolveLocalUrl(url: string, context = 'unknown'): string {
  if (Platform.OS === 'web') {
    debugLocalUrlResolution(context, {
      inputUrl: url,
      platform: Platform.OS,
      reason: 'web uses configured/window URL without native host rewrite',
      resolvedUrl: url,
    });
    return url;
  }

  const devHostMetadata = getNativeDevHostMetadata();
  const resolution = resolveLocalUrlForPlatform(url, Platform.OS, devHostMetadata);

  debugLocalUrlResolution(context, {
    ...devHostMetadata,
    configuredHostname: (() => {
      try {
        return new URL(url).hostname;
      } catch {
        return null;
      }
    })(),
    inputUrl: url,
    platform: Platform.OS,
    reason: resolution.reason,
    resolvedUrl: resolution.resolvedUrl,
  });
  return resolution.resolvedUrl;
}
